import { EvaluationHistoryFilterFilteredStatusOptions } from "./../../../model/evaluation-history";
import { SessionService } from "./../../../../store/session/session.service";
import { Permission, UserInfo } from "./../../../../store/session/session.store";
import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, signal, computed, ViewChild } from "@angular/core";
import { SelectableOption } from "@app-services/open.service";
import { MatCheckboxChange } from "@angular/material/checkbox";
import {
  EvaluationHistoryFilter,
  EvaluationHistoryFilterDestinationOptions,
} from "@app-model/evaluation-history";
import { Observable } from "rxjs";
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { EvalDealerHelper } from "@app/views/private/admin/model/advertisers";
import { FormControl } from "@angular/forms";
import { EvaluationService } from "@app-services/evaluation.service";
import { EvalTypeLabel, OwnerTypeLabel, ReasonsArchivesLabel } from "@app-model/evaluation";
import { SegmentLabel } from "@app-model/vehicle";
import { IdName } from "@app-services/oem-dealer.service";
import { NgxDaterangepickerBootstrapDirective } from "ngx-daterangepicker-bootstrap";
import dayjs, { Dayjs } from "dayjs";

@Component({
  selector: "app-evaluation-history-filter",
  templateUrl: "./evaluation-history-filter.component.html",
  styleUrls: ["./evaluation-history-filter.component.scss"],
})
export class EvaluationHistoryFilterComponent implements OnInit, OnChanges {

  @Input() filter: EvaluationHistoryFilter;
  @Input() showFinishedStatus: boolean;
  @Output() filterChanged: EventEmitter<
    EvaluationHistoryFilter
  > = new EventEmitter();

  statusOptions: SelectableOption<
    EvaluationHistoryFilterFilteredStatusOptions
  >[];
  destinationsOptions: SelectableOption<
    EvaluationHistoryFilterDestinationOptions
  >[];

  selectedDateRange: { startDate: Dayjs | null; endDate: Dayjs | null } | null = {
    startDate: dayjs().startOf('month'),
    endDate: dayjs(),
  };

  private isPeriodCleared = false;

  readonly ranges: Record<string, [Dayjs, Dayjs]> = {
    'Hoje': [dayjs().startOf('day'), dayjs().endOf('day')],
    'Ontem': [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
    'Últimos 7 dias': [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')],
    'Últimos 30 dias': [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')],
    'Mês atual': [dayjs().startOf('month'), dayjs().endOf('day')],
    'Mês passado': [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().subtract(1, 'month').endOf('month'),
    ],
  };

  @ViewChild(NgxDaterangepickerBootstrapDirective) private readonly periodPicker?: NgxDaterangepickerBootstrapDirective;

  get hasPeriodSelected(): boolean {
    return !!(this.selectedDateRange?.startDate && this.selectedDateRange?.endDate);
  }

  readonly userInfo = signal<UserInfo | undefined>(undefined);
  readonly canShowPricingUserFilter = computed(() => this.userInfo()?.canViewPricingUserFilter() ?? false);
  evalDealerOptions: Observable<EvalDealerHelper[]>;
  allEvalTypes = EvalTypeLabel.allTypes;
  allOwnerTypes = OwnerTypeLabel.allTypes;
  allReasonArchive = ReasonsArchivesLabel.allTypes;
  allSegments = SegmentLabel.allSegments;

  evalDealerControl: FormControl = new FormControl();
  pricingUserOptions: IdName[] = [];
  pricingUserControl = new FormControl<string[]>([]);

  constructor(
    private sessionService: SessionService,
    private evaluationService: EvaluationService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] && !changes['filter'].firstChange) {
      this.initStatusOptions();
      this.checkIfThereIsDate();
      this.refreshPricingUserControl();
    }
  }

  ngOnInit() {
      this.sessionService.getUser().subscribe(u => {
      this.userInfo.set(u);
      this.sanitizeArchivedFilters();
      this.initFilterOptions();
      this.loadPricingUsers(u);
    });

    this.checkIfThereIsDate();
    this.refreshPricingUserControl();
  }

  private loadPricingUsers(user?: UserInfo): void {
    if (!user?.canViewPricingUserFilter()) {
      this.filter.pricingUserIds = undefined;
      this.refreshPricingUserControl();
      return;
    }

    this.evaluationService.getPricingUsers().subscribe(users => {
      this.pricingUserOptions = users ?? [];
    });
  }

  private initFilterOptions(): void {
    this.initStatusOptions();
    if (!this.userInfo()?.isAdmin()) {
      this.refreshEvalDealerOptions();
    }

    this.evalDealerControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(query => {
        this.onChangeEvalDealerQuery(query);
      });
  }

  private refreshEvalDealerOptions(): void {
    this.evalDealerOptions = this.evaluationService.searchEvalDealers(this.evalDealerControl.value);
  }


  onChangeEvalDealerQuery(value: string | EvalDealerHelper) {
    if (typeof value !== 'string') {
      this.selectEvalDealer(value);
      this.evalDealerControl.setValue(value.name);
    } else {
      this.refreshEvalDealerOptions();
    }
  }

  updateStatus(
    option: EvaluationHistoryFilterFilteredStatusOptions,
    event: MatCheckboxChange
  ): void {
    if (!this.filter.statuses) {
      this.filter.statuses = [];
    }
    const key = this.getEnumKey(
      option,
      EvaluationHistoryFilterFilteredStatusOptions
    );
    if (event.checked && this.filter.statuses.indexOf(key) < 0) {
      this.filter.statuses.push(key);
    }

    if (!event.checked) {
      this.filter.statuses = this.filter.statuses.filter((f) => f !== key);
    }
    this.submitFilter();
  }

  updateDestination(
    option: EvaluationHistoryFilterDestinationOptions,
    event: MatCheckboxChange
  ) {
    if (!this.filter.destinations) {
      this.filter.destinations = [];
    }
    const key = this.getEnumKey(
      option,
      EvaluationHistoryFilterDestinationOptions
    );
    if (event.checked && this.filter.statuses.indexOf(key) < 0) {
      this.filter.destinations.push(key);
      this.filter.statuses = ['BOUGHT']
    }

    if (!event.checked) {
      this.filter.destinations = this.filter.destinations.filter(
        (f) => f !== key
      );

      if(!this.filter.destinations || this.filter.destinations.length == 0){
        this.filter.statuses = [];
      }else{
        if(!this.filter.statuses){
          this.filter.statuses = [];
        }
      }
    }
    this.submitFilter();
  }

  private initStatusOptions() {
    this.statusOptions = [];
    Object.keys(EvaluationHistoryFilterFilteredStatusOptions)
      .filter((key) => this.canViewArchivedEvaluations() || key !== 'ARCHIVED')
      .forEach((key) =>
      this.addEnumOption(
        this.statusOptions,
        this.filter.statuses,
        EvaluationHistoryFilterFilteredStatusOptions[key],
        EvaluationHistoryFilterFilteredStatusOptions
      )
    );
    this.destinationsOptions = [];
    Object.keys(EvaluationHistoryFilterDestinationOptions).forEach((key) =>
      this.addEnumOption(
        this.destinationsOptions,
        this.filter.destinations,
        EvaluationHistoryFilterDestinationOptions[key],
        EvaluationHistoryFilterDestinationOptions
      )
    );
  }

  private addEnumOption(
    selectableOptions: SelectableOption<any>[],
    selectedOptions: string[],
    option: any,
    enumType: any
  ): void {
    const selectableOption = new SelectableOption<any>();
    selectableOption.option = option;
    selectableOption.selected =
      selectedOptions &&
      selectedOptions.indexOf(this.getEnumKey(option, enumType)) >= 0;
    selectableOptions.push(selectableOption);
  }

  submitFilter(): void {
    this.sanitizeArchivedFilters();
    this.filterChanged.emit(this.filter);
  }

  onSearchEnter(event: Event): void {
    (event.target as HTMLInputElement | null)?.blur();
  }

  canViewArchivedEvaluations(): boolean {
    return this.userInfo()?.hasPermissionOrLegacy(Permission.VIEW_ARCHIVED_EVALUATIONS) ?? false;
  }

  private getEnumKey(enumValue: any, enumType: any): string {
    const idx = Object.values(enumType).indexOf(enumValue);
    return Object.keys(enumType)[idx];
  }

  changeSegment(newSegment: string): void {
    if (!this.filter.segments) {
      this.filter.segments = [];
    }

    if (this.filter.segments.indexOf(newSegment) >= 0) {
      this.filter.segments = this.filter.segments.filter(s => s !== newSegment);
    } else {
      this.filter.segments.push(newSegment);
    }
    this.submitFilter();
    this.initFilterOptions();
  }

  checkResetEvalDealer(): void {
    if (this.evalDealerControl.value === ''
      && this.filter.evalDealerIds !== undefined
      && this.filter.evalDealerIds.length > 0) {

      this.filter.evalDealerIds = undefined;
      this.submitFilter();
    }
  }


  selectEvalDealer(evalDealer: EvalDealerHelper) {
    this.filter.evalDealerIds = [evalDealer.id];
    this.evalDealerControl.setValue(evalDealer.name);
    this.submitFilter();
  }

  applyPricingUserFilter(): void {
    const selectedIds = this.pricingUserControl.value ?? [];
    if (!selectedIds.length) {
      this.refreshPricingUserControl();
      return;
    }
    this.filter.pricingUserIds = selectedIds;
    this.submitFilter();
  }

  removePricingUser(userId: string): void {
    this.filter.pricingUserIds = this.filter.pricingUserIds?.filter(id => id !== userId);
    if (!this.filter.pricingUserIds?.length) {
      this.filter.pricingUserIds = undefined;
    }
    this.pricingUserControl.setValue(this.filter.pricingUserIds ?? []);
    this.submitFilter();
  }

  getPricingUserName(userId: string): string {
    return this.pricingUserOptions.find(u => u.id === userId)?.name ?? userId;
  }

  private refreshPricingUserControl(): void {
    this.pricingUserControl.setValue(this.filter.pricingUserIds ?? []);
  }

  getEvalTypeLabel(type: string): string {
    return EvalTypeLabel.getLabel(type);
  }

  isTypeSelected(type: string): boolean {
    return this.filter.evalTypes !== undefined && this.filter.evalTypes.findIndex(t => t === type) >= 0;
  }

  toggleType(type: string): void {
    if (this.isTypeSelected(type)) {
      this.filter.evalTypes = this.filter.evalTypes.filter(t => t !== type);
    } else {
      if (this.filter.evalTypes === undefined) {
        this.filter.evalTypes = [];
      }

      this.filter.evalTypes.push(type);
    }

    this.submitFilter();
  }

  getOwnerTypeLabel(type: string): string {
    return OwnerTypeLabel.getLabel(type);
  }

  isOwnerTypeSelected(type: string): boolean {
    return this.filter.ownerTypes !== undefined && this.filter.ownerTypes.findIndex(t => t === type) >= 0;
  }

  toggleOwnerType(type: string): void {
    if (this.isOwnerTypeSelected(type)) {
      this.filter.ownerTypes = this.filter.ownerTypes.filter(t => t !== type);
    } else {
      if (this.filter.ownerTypes === undefined) {
        this.filter.ownerTypes = [];
      }

      this.filter.ownerTypes.push(type);
    }

    this.submitFilter();
  }

  getReasonArchiveLabel(reason: string): string {
    return ReasonsArchivesLabel.getLabel(reason);
  }

  isReasonArchiveSelected(reason: string): boolean {
    if(typeof(this.filter.reasonArchives) === "string"){
      this.filter.reasonArchives = [];
      this.filter.reasonArchives.push(reason);
    }
    return this.filter.reasonArchives !== undefined && this.filter.reasonArchives.findIndex(r => r === reason) >= 0;
  }

  toggleReasonArchive(reason: string): void {
    if (this.isReasonArchiveSelected(reason)) {
      this.filter.reasonArchives = this.filter.reasonArchives.filter(r => r !== reason);
    } else {
      if (this.filter.reasonArchives === undefined) {
        this.filter.reasonArchives = [];
      }

      this.filter.reasonArchives.push(reason);
    }

    this.submitFilter();
  }

  private sanitizeArchivedFilters(): void {
    if (this.canViewArchivedEvaluations()) {
      return;
    }

    if (this.filter.statuses?.length) {
      this.filter.statuses = this.filter.statuses.filter(status => status !== 'ARCHIVED');
    }

    if (this.filter.reasonArchives?.length) {
      this.filter.reasonArchives = undefined;
    }
  }


  clearPeriodRange(): void {
    this.isPeriodCleared = true;
    this.filter.updatedAtIni = undefined;
    this.filter.updatedAtEnd = undefined;
    this.filter.pageControl.page = 0;
    this.selectedDateRange = null;
    this.periodPicker?.clear();
    this.submitFilter();
  }

  selectedDate(event: { startDate?: Dayjs | null; endDate?: Dayjs | null }): void {
    if (!event?.startDate || !event?.endDate) {
      return;
    }
    this.isPeriodCleared = false;
    this.setRange(event.startDate, event.endDate);
    this.checkIfThereIsDate();
    this.submitFilter();
  }

  setRange(start: Dayjs, end: Dayjs): void {
    this.filter.updatedAtIni = start.startOf('day').format();
    this.filter.updatedAtEnd = end.startOf('day').add(1, 'day').format();

    this.filter.pageControl.page = 0;
  }

  private checkIfThereIsDate(): void {
    if (this.filter?.updatedAtIni && this.filter?.updatedAtEnd) {
      const startDate = dayjs(this.filter.updatedAtIni);
      const endDate = dayjs(this.filter.updatedAtEnd).subtract(1, 'day');
      this.selectedDateRange = { startDate, endDate };
      this.isPeriodCleared = false;
    } else if (this.isPeriodCleared) {
      this.selectedDateRange = null;
    } else {
      const startDate = dayjs().startOf('month');
      const endDate = dayjs();
      this.selectedDateRange = { startDate, endDate };
    }
  }

}
